import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Rating,
  Stack,
  Divider,
  Container,
  Grid,
  useTheme,
} from "@mui/material";
import congratulationsImage from "../../../assets/images/icons/congratulation.svg";
import DownloadIcon from "../../../assets/images/icons/download.svg";
import CircularProgress from "@mui/material/CircularProgress";
import CertificateIcon from "../../../assets/images/icons/certificate.svg";
import API from "../../../api/Axios";
import DataLoader from "../../../shared/DataLoader/DataLoader";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function CompleteSurvey() {
  const { t } = useTranslation("home");
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [loading, setLoading] = useState(false);
  const [certificateList, setCertificateList] = useState<any>([]);
  const [surveyInfo, setSurveyInfo] = useState<any>({});
  const navigate = useNavigate();
  const [downloadingCertificates, setDownloadingCertificates] = useState<
    Record<string, boolean>
  >({});

  const handleDownloadCertificate = async (id: any, name: string) => {
    if (!id || downloadingCertificates[id]) {
      return;
    }

    setDownloadingCertificates((prev) => ({ ...prev, [id]: true }));
    try {
      const response = await API.get(`/certificate/download/${id}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName = `${name}.pdf`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[Certificate Download] error", err);
    } finally {
      setDownloadingCertificates((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    }
  };

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setLoading(true);
        const response = await API.get("/certificate/list");
        const certificateData = response?.data?.data;

        if (certificateData?.rating_certificate_disable === true) {
          navigate("/school-dashboard", { replace: true });
          return;
        }

        setCertificateList(certificateData?.certificate_list);
        setSurveyInfo(certificateData?.survey_survey_info);
        if (
          certificateData?.survey_survey_info?.survey_status !== "submitted"
        ) {
          navigate("/school-dashboard", { replace: true });
        }
      } catch (err) {
        console.error("[Certificate List] error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificates();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: isDarkMode ? "#1A1D21" : "#E9FFF3",
          minHeight: "calc(100vh - 210px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <DataLoader />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: isDarkMode
          ? "#1A1D21"
          : "linear-gradient(180deg, #DFFCF5 0%, #EEFFF0 100%)",
        minHeight: "calc(100vh - 190px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        pt: { xs: 2.5, md: 4 },
      }}
    >
      <Container
        maxWidth={false}
        sx={{ width: { xs: "100%", md: "88%" }, px: { xs: 2, md: 0 } }}
      >
        <Box
          sx={{
            minHeight: { xs: "calc(100vh - 215px)", md: "calc(100vh - 230px)" },
            border: `1px solid ${isDarkMode ? "#596268" : "#49DEC8"}`,
            borderRadius: { xs: "22px 22px 0 0", md: "34px 34px 0 0" },
            borderBottom: "none",
            background: isDarkMode
              ? "linear-gradient(180deg, #22282D 0%, #29322F 100%)"
              : "linear-gradient(180deg, rgba(248,252,255,.96) 0%, rgba(235,250,237,.96) 100%)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: { xs: 2, md: 5 },
              py: { xs: 5, md: 7 },
            }}
          >
            <Box sx={{ textAlign: "center", mb: 1.5 }}>
              <Box
                component="img"
                src={congratulationsImage}
                alt="congratulations"
                sx={{
                  width: { xs: 90, md: 118 },
                  height: "auto",
                  opacity: isDarkMode ? 0.7 : 0.55,
                  filter: isDarkMode ? "grayscale(1) brightness(1.5)" : "grayscale(1)",
                }}
              />
            </Box>

            <Typography
              variant="h5"
              sx={{
                color: isDarkMode ? "#FFFFFF" : "#0F202B",
                fontSize: {
                  xs: `calc(22px * var(--dynamic-font-size, 1)) !important`,
                  md: `calc(30px * var(--dynamic-font-size, 1)) !important`,
                },
                fontWeight: 700,
                m: 0,
                textAlign: "center",
              }}
            >
              {t("completeSurvey.congratulations")}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: isDarkMode ? "#E0E3E1" : "#111820",
                textAlign: "center",
                fontSize: {
                  xs: `calc(14px * var(--dynamic-font-size, 1)) !important`,
                  md: `calc(19px * var(--dynamic-font-size, 1)) !important`,
                },
                lineHeight: 2,
                fontWeight: 500,
                mt: { xs: 2, md: 3 },
                mb: certificateList?.length ? 3 : 0,
                maxWidth: 900,
              }}
            >
              <strong>{surveyInfo?.school_name || "School"}</strong>{" "}
              {t("completeSurvey.successMessage")}
              <br />
              Self-Assessment for <strong>2025</strong> with Reference ID{" "}
              <strong>SHVR-{surveyInfo?.reference_number}</strong>
            </Typography>

        {Boolean(surveyInfo?.star_rating) && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              backgroundColor: isDarkMode ? "#30373B" : "#fff",
              borderRadius: "10px",
              px: 5,
              py: 1.5,
              boxShadow: "0px 0px 7.1px 2px #00000008",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              width: "fit-content",
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: `calc(14px * var(--dynamic-font-size, 1)) !important`,
                  md: `calc(20px * var(--dynamic-font-size, 1)) !important`,
                },
                fontWeight: "500",
              }}
            >
              {t("completeSurvey.yourSchoolSurveyRating")}
            </Typography>
            <Rating
              name="read-only"
              value={surveyInfo?.star_rating}
              readOnly
              sx={{
                color: "#1A5F20",
              }}
            />
          </Box>
        </Box>
        )}

        {certificateList?.length > 0 && (
        <Box sx={{ pt: 3, width: "100%", maxWidth: 1200 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: {
                xs: `calc(20px * var(--dynamic-font-size, 1)) !important`,
                md: `calc(28px * var(--dynamic-font-size, 1)) !important`,
              },
              fontWeight: "700",
              color: isDarkMode ? "#FFFFFF" : "#0F202B",
              mb: 2,
            }}
          >
            {t("completeSurvey.yourCertificates")}
          </Typography>

          <Box
            sx={{
              border: `1px solid ${isDarkMode ? "#596268" : "#D9D9D9"}`,
              borderRadius: "20px",
              padding: { xs: 2, md: 4 },
            }}
          >
            <Grid container spacing={4}>
              {certificateList?.map((certificate: any, index: number) => {
                return (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Card
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 2,
                        boxShadow: "0px 0px 7.1px 2px #00000008",
                        borderRadius: "10px",
                        backgroundColor: isDarkMode ? "#30373B" : "#fff",
                        // flexWrap: "wrap",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: 2, sm: 0 },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          flexDirection: {
                            xs: "column",
                            sm: "row",
                            whiteSpace: "nowrap",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: "60px",
                            height: "60px",
                            backgroundColor: "#E7E7E7",
                            borderRadius: "8px",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <img
                            src={CertificateIcon}
                            alt="certificate"
                            height={40}
                            width={40}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: {
                              xs: `calc(14px * var(--dynamic-font-size, 1)) !important`,
                              md: `calc(17px * var(--dynamic-font-size, 1)) !important`,
                            },
                            fontWeight: "500",
                            whiteSpace: "wrap",
                            wordBreak: "break-all",
                          }}
                        >
                          {certificate?.title || "NA"}
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        sx={{
                          backgroundColor: `${
                            isDarkMode ? "#30373B" : "#fff"
                          } !important`,
                          boxShadow: "none",
                          minWidth: "auto",
                          padding: { xs: 0, sm: "10px" },
                          "&:hover": {
                            backgroundColor: "transparent",
                            boxShadow: "none",
                          },
                        }}
                        disabled={Boolean(
                          downloadingCertificates[certificate?.id]
                        )}
                        onClick={() =>
                          handleDownloadCertificate(
                            certificate?.id,
                            certificate?.title
                          )
                        }
                      >
                        {downloadingCertificates[certificate?.id] ? (
                          <CircularProgress size={16} sx={{ color: "blue" }} />
                        ) : (
                          <img
                            src={DownloadIcon}
                            alt="download"
                            style={{ width: 24, height: 24 }}
                          />
                        )}
                      </Button>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
        )}
          </Box>

          {/* <Box
            component="footer"
            sx={{
              py: 2.5,
              px: 2,
              borderTop: `1px solid ${
                isDarkMode ? "#465047" : "#D3DDD5"
              }`,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                color: isDarkMode ? "#AEB4B0" : "#737A76",
                fontSize: {
                  xs: `calc(.75rem * var(--dynamic-font-size, 1))`,
                  md: `calc(.9rem * var(--dynamic-font-size, 1))`,
                },
              }}
            >
              © {new Date().getFullYear()} Ministry of Education, Govt. of
              India. All rights reserved.
            </Typography>
          </Box> */}
        </Box>
      </Container>
    </Box>
  );
}
